import React, { createContext, useContext } from 'react';
import type { FindingAdditionalInfo } from '../../../../helpers/findingsProvenance';

export interface PatientInfo {
  name: string;
  sex: string;
  age: string;
  birthday: string;
}

export interface PregnancyInfo {
  id?: number;
  estimatedDueDate: string;
  lastMenstrualPeriod: string;
  pregnancyType: string;
  pregnancyStatus: string;
  closedAt?: string | null;
  outcomeDate?: string | null;
  gestationalAgeWeeks?: number;
  gestationalAgeDays?: number;
  datingMethod?: string;
  preg_crl?: number;
  preg_bpd?: number;
  preg_hc?: number;
  preg_ac?: number;
  preg_fl?: number;
  grossesse_classee_a_risque?: boolean | null;
  risque_anemie?: boolean;
  risque_hta?: boolean;
  risque_diabete?: boolean;
  risque_cardiopathie?: boolean;
  risque_metrorragie?: boolean;
  risque_infection?: boolean;
  risque_preeclampsie?: boolean;
  risque_eclampsie?: boolean;
  risque_autre_details?: string | null;
}

export interface StudyInfo {
  studyDate: string;
  studyId: string;
  studyType: string;
  category: string;
  series: Array<{ serie_instance_uid: string; type: string }>;
}

export interface ViewImage {
  display_name: string;
  image_base64: string;
  frame_id: string;
  view_code?: string;
  view_def_id?: number | null;
  /** Source instance metadata used to open the exact frame in the segmentation editor. */
  sop_instance_uid: string;
  referenced_sop_instance_uid?: string;
  serie_instance_uid: string;
  frame_number: number;
  format_file: 'DICOM' | 'VIDEO';
  frame_count?: number;
  modality?: string | null;
  sop_class_uid?: string | null;
  fps?: number | null;
  width?: number | null;
  height?: number | null;
}

export interface MeasurementSource {
  meas_key: string;
  meas_label: string;
  value?: number | null;
  is_manual?: boolean | null;
  frame_id: string;
  frame_number?: number | null;
  sop_instance_uid: string;
  referenced_sop_instance_uid?: string | null;
  serie_instance_uid: string;
  view_def_id?: number | null;
  view_code?: string | null;
  display_name?: string | null;
  format_file?: string | null;
  frame_count?: number | null;
  modality?: string | null;
  sop_class_uid?: string | null;
  fps?: number | null;
  width?: number | null;
  height?: number | null;
}

export interface StudyHistoryEntry {
  study_instance_uid: string;
  study_date: string | null;
  study_report: {
    measurements: {
      bpd_cm: number | null;
      hc_cm: number | null;
      ac_cm: number | null;
      fl_cm: number | null;
      ga_bpd: number | null;
      ga_hc: number | null;
      ga_ac: number | null;
      ga_fl: number | null;
      ga_aua: number | null;
      efw_g: number | null;
    };
  } | null;
}

export type AnatomyStatus = 'visualized' | 'not_visualized';
export type AnatomySource = 'ai' | 'manual' | null;
export type ReportDetailMode = 'minimal' | 'full';

export interface FetalAnatomyItem {
  category_id: number | null;
  category_code: string;
  structure_name: string;
  region: string;
  status: AnatomyStatus;
  source: AnatomySource;
}

export interface ReportDataValues {
  patientInfo?: PatientInfo;
  pregnancyInfo?: PregnancyInfo;
  studyInfo?: StudyInfo;
  studyUID?: string;
  views?: Array<{ view_name: string; frameviewid: string }>;
  viewImages?: ViewImage[];
  measurementSources?: MeasurementSource[];
  additionalInfo?: FindingAdditionalInfo;
  isLoading: boolean;

  biometryValues: Record<string, number | undefined>;
  measurementValues: Record<
    string,
    number | string | boolean | null | undefined
  >;
  studyReportFields: Record<
    string,
    number | string | boolean | null | undefined
  >;
  fetalAssessment: {
    presentation?: string;
    facing?: string;
    heartRate?: number;
    placentaLocalization?: string;
    placentaCervixClass?: string;
  };
  efw: {
    efwGrams?: number;
  };
  clinicalImpression: {
    findings?: string;
    impression?: string;
    recommendations?: string;
  };
  screening: {
    gestationalDiabetes?: boolean | null;
    preEclampsia?: boolean | null;
    sexPrediction?: string | null;
    isTwins?: boolean | null;
  };
  gaPredictions: {
    predGaBpd?: number;
    predGaHc?: number;
    predGaAc?: number;
    predGaFl?: number;
    predGaAua?: number;
    predEdd?: number;
  };
  pregnancyStudiesHistory: StudyHistoryEntry[];
  fetalAnatomy: FetalAnatomyItem[];
  onAnatomyStatusOverride?: (
    categoryCode: string,
    status: AnatomyStatus,
    item?: FetalAnatomyItem
  ) => void;
  reportDetailMode: ReportDetailMode;
  onReportDetailModeChange?: (mode: ReportDetailMode) => void;

  onUpdateFindings?: (updates: Record<string, unknown>) => void;
  isSaving?: boolean;
  isLocked?: boolean;

  patientId?: string;
  onPregnancyUpdate?: (updatedPregnancy: Record<string, unknown>) => void;

  currentPregnancyId?: number;
  currentLmp?: string;
  currentEdd?: string;
  currentGaWeeks?: number;
  currentGaDays?: number;

  onDeleteView?: (frameViewId: string) => void;
  isDeletingView?: boolean;
}

const defaultValues: ReportDataValues = {
  isLoading: false,
  biometryValues: {},
  measurementValues: {},
  studyReportFields: {},
  fetalAssessment: {},
  efw: {},
  clinicalImpression: {},
  screening: {},
  gaPredictions: {},
  pregnancyStudiesHistory: [],
  fetalAnatomy: [],
  reportDetailMode: 'minimal',
};

const ReportDataContext = createContext<ReportDataValues>(defaultValues);

export function ReportDataProvider({
  value,
  children,
}: {
  value: ReportDataValues;
  children: React.ReactNode;
}) {
  return (
    <ReportDataContext.Provider value={value}>
      {children}
    </ReportDataContext.Provider>
  );
}

export function useReportData() {
  return useContext(ReportDataContext);
}
